import { cs } from '../../lib/currency';
import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { ArrowUp, ArrowDown, ChevronsUpDown, CalendarRange } from 'lucide-react';
import api from '../../services/api';

const TYPE_OPTIONS = [
  { value: '', label: 'All types' },
  { value: 'lane', label: 'Lane Booking' },
  { value: 'small_group', label: 'Small Group' },
  { value: 'class', label: 'Classes & lane holds' },
  { value: 'camp', label: 'Camp' },
  { value: 'tournament', label: 'Tournament' },
];

const STATUS_OPTIONS = ['', 'paid', 'unpaid', 'pending', 'failed', 'refunded'];

const COLUMNS = [
  { key: 'person_name', label: 'Customer / Parent', sortable: true },
  { key: 'type', label: 'Type', sortable: true },
  { key: 'date', label: 'Date', sortable: true },
  { key: 'time', label: 'Time', sortable: false },
  { key: 'duration', label: 'Duration', sortable: true },
  { key: 'cost', label: 'Cost', sortable: true },
  { key: 'payment_status', label: 'Payment', sortable: true },
];

function monthBounds() {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const pad = (n) => String(n).padStart(2, '0');
  const start = `${y}-${pad(m + 1)}-01`;
  const last = new Date(y, m + 1, 0).getDate();
  const end = `${y}-${pad(m + 1)}-${pad(last)}`;
  return { start, end };
}

const statusVariant = (s) => {
  const v = (s || '').toLowerCase();
  if (v === 'paid') return 'default';
  if (v === 'refunded') return 'secondary';
  if (v === 'failed' || v === 'unpaid') return 'destructive';
  return 'outline';
};

export default function AllBookings() {
  const navigate = useNavigate();
  const { start: defStart, end: defEnd } = monthBounds();
  const [filters, setFilters] = useState({ type: '', status: '', q: '', start: defStart, end: defEnd });
  const [sortBy, setSortBy] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [data, setData] = useState({ rows: [], total: 0, has_more: false });
  const [loading, setLoading] = useState(false);

  const reqId = useRef(0);
  const fetchData = useCallback(async () => {
    const id = ++reqId.current;  // only the latest request may update the table
    setLoading(true);
    try {
      const params = {
        sort_by: sortBy, sort_dir: sortDir, page, page_size: pageSize,
      };
      if (filters.type) params.types = filters.type;
      if (filters.status) params.status = filters.status;
      if (filters.q) params.q = filters.q;
      if (filters.start) params.start = filters.start;
      if (filters.end) params.end = filters.end;
      const { data: res } = await api.get('/admin/bookings/all', { params });
      if (id === reqId.current) setData(res);
    } catch (e) {
      if (id === reqId.current) setData({ rows: [], total: 0, has_more: false });
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, [filters, sortBy, sortDir, page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const onSort = (key) => {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir('desc');
    }
    setPage(1);
  };

  const setFilter = (k, v) => { setFilters((f) => ({ ...f, [k]: v })); setPage(1); };

  const personHref = (r) => r.person_id
    ? (r.person_kind === 'customer' ? `/admin/customers/${r.person_id}` : `/admin/parents/${r.person_id}`)
    : null;

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / pageSize));

  const sortIcon = (key) => {
    if (sortBy !== key) return <ChevronsUpDown className="inline h-3.5 w-3.5 opacity-40 ml-1" />;
    return sortDir === 'asc'
      ? <ArrowUp className="inline h-3.5 w-3.5 ml-1" />
      : <ArrowDown className="inline h-3.5 w-3.5 ml-1" />;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="all-bookings-page">
        <div className="flex items-center gap-3">
          <CalendarRange className="h-6 w-6 text-foreground" />
          <div>
            <h1 className="text-2xl font-semibold text-foreground">All Bookings</h1>
            <p className="text-sm text-muted-foreground">Every lane booking, session, class, camp and tournament in one place.</p>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="all-bookings-filters">
            <div>
              <label className="text-xs text-muted-foreground">Type</label>
              <select
                data-testid="filter-type"
                className="mt-1 w-full h-9 rounded-md border border-input bg-background text-foreground px-2 text-sm"
                value={filters.type}
                onChange={(e) => setFilter('type', e.target.value)}
              >
                {TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Payment status</label>
              <select
                data-testid="filter-status"
                className="mt-1 w-full h-9 rounded-md border border-input bg-background text-foreground px-2 text-sm"
                value={filters.status}
                onChange={(e) => setFilter('status', e.target.value)}
              >
                {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s === '' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">From</label>
              <Input type="date" data-testid="filter-start" className="mt-1 h-9" value={filters.start}
                onChange={(e) => setFilter('start', e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">To</label>
              <Input type="date" data-testid="filter-end" className="mt-1 h-9" value={filters.end}
                onChange={(e) => setFilter('end', e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Search name</label>
              <Input data-testid="filter-search" className="mt-1 h-9" placeholder="Customer / parent"
                value={filters.q} onChange={(e) => setFilter('q', e.target.value)} />
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm" data-testid="all-bookings-table">
              <thead>
                <tr className="border-b bg-muted text-left text-muted-foreground">
                  {COLUMNS.map((c) => (
                    <th key={c.key} className="px-4 py-3 font-medium whitespace-nowrap">
                      {c.sortable ? (
                        <button type="button" onClick={() => onSort(c.key)} className="inline-flex items-center hover:text-foreground"
                          data-testid={`sort-${c.key}`}>
                          {c.label}{sortIcon(c.key)}
                        </button>
                      ) : c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={COLUMNS.length} className="px-4 py-10 text-center text-muted-foreground">Loading…</td></tr>
                ) : data.rows.length === 0 ? (
                  <tr><td colSpan={COLUMNS.length} className="px-4 py-10 text-center text-muted-foreground" data-testid="all-bookings-empty">No bookings in this range.</td></tr>
                ) : data.rows.map((r) => {
                  const href = personHref(r);
                  return (
                    <tr key={`${r.source_type}-${r.id}`} className="border-b hover:bg-muted/50" data-testid="booking-row">
                      <td className="px-4 py-3 whitespace-nowrap">
                        {href ? (
                          <button className="text-blue-600 hover:underline font-medium" onClick={() => navigate(href)}
                            data-testid={`booking-person-${r.id}`}>{r.person_name}</button>
                        ) : <span className="text-foreground">{r.person_name}</span>}
                        {r.player_name ? <span className="block text-xs text-muted-foreground">{r.player_name}</span> : null}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap"><Badge variant="outline">{r.type_label}</Badge></td>
                      <td className="px-4 py-3 whitespace-nowrap">{r.date || '—'}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{r.time || '—'}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{r.duration ? `${r.duration} min` : '—'}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {href ? (
                          <button className="text-blue-600 hover:underline" onClick={() => navigate(`${href}?tab=billing`)}
                            title={r.invoice_number || ''}
                            data-testid={`booking-cost-${r.id}`}>{cs()}{Number(r.cost || 0).toFixed(2)}</button>
                        ) : <span>{r.person_kind === 'lane_block' ? '—' : `${cs()}${Number(r.cost || 0).toFixed(2)}`}</span>}
                        {r.invoice_number ? (
                          <div className="text-xs text-muted-foreground font-mono" data-testid={`booking-invno-${r.id}`}>{r.invoice_number}</div>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <Badge variant={statusVariant(r.payment_status)} data-testid={`booking-status-${r.id}`}>{r.payment_status || '—'}</Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Pagination */}
        <div className="flex items-center justify-between" data-testid="all-bookings-pagination">
          <span className="text-sm text-muted-foreground">
            {data.total} booking{data.total === 1 ? '' : 's'} · page {page} of {totalPages}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="page-prev">Previous</Button>
            <Button variant="outline" size="sm" disabled={!data.has_more || loading}
              onClick={() => setPage((p) => p + 1)} data-testid="page-next">Next</Button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
